import { useState, useEffect, useCallback, useRef } from "react";
import { Card, Table, Button } from "antd";
import Box from "../components/Box";
import SearchInput from "../components/SearchInput";
import { blockUrl, txUrl } from "../utils/urls";
import axios from "../utils/axios";

function TransactionTable() {
  // Ref
  const initialized = useRef(false);

  // State
  const [lastBlockHeight, setLastBlockHeight] = useState(null);
  const [currentBlockHeight, setCurrentBlockHeight] = useState(null);

  const [blocks, setBlocks] = useState([]);
  const [txHashes, setTxHashes] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchedTransaction, setSearchedTransaction] = useState(null);
  const [isSearched, setIsSearched] = useState(false);

  const getBlockHeightFromBlockHash = (blockHash) => {
    const block = blocks.find((block) => block.block_id === blockHash);
    if (block) {
      return block.header.height;
    }

    return "-";
  };

  const getBlockTimeFromBlockHash = (blockHash) => {
    const block = blocks.find((block) => block.block_id === blockHash);
    if (block) {
      return block.header.time;
    }

    return "-";
  };

  const onBlockHeightColumnClick = (blockHeight) => {
    window.open(`/blocks/${blockHeight}`, "_blank");
  };

  const onTransactionHashColumnClick = (txHash) => {
    window.open(`/tx/${txHash}`, "_blank");
  };

  const columns = [
    {
      title: "Block Height",
      render: (record) => {
        return (
          <Box>
            <Button
              type="link"
              onClick={() => onBlockHeightColumnClick(record.block_height)}
            >
              {record.block_height}
            </Button>
          </Box>
        );
      },
    },
    {
      title: "Tx hash",
      render(record) {
        return (
          <Box>
            <Button
              onClick={() => onTransactionHashColumnClick(record.hash)}
              type="link"
            >
              {record.hash}
            </Button>
          </Box>
        );
      },
    },
    {
      title: "Type",
      render: (record) => {
        return <Box>{record.tx_type}</Box>;
      },
    },
    {
      title: "Datetime",
      render: (record) => {
        return <Box>{getBlockTimeFromBlockHash(record.block_id)}</Box>;
      },
    },
  ];

  const fetchLastBlock = useCallback(async () => {
    const url = `${blockUrl}/last`;

    axios.get(url).then((response) => {
      if (response.data) {
        const lastBlock = response.data;
        const lastBlockHeight = lastBlock.last_commit.height;

        if (lastBlockHeight) {
          setLastBlockHeight(lastBlockHeight);
        }
      }
    });
  }, []);

  const fetchTransaction = useCallback(async (txHash) => {
    const url = `${txUrl}/${txHash}`;

    axios.get(url).then((response) => {
      if (response.data) {
        const transaction = response.data;
        setTransactions((prevTransactions) => {
          return [...prevTransactions, transaction];
        });
      }
    });
  }, []);

  const fetchBlock = useCallback(
    (blockHeight) => {
      const url = `${blockUrl}/height/${blockHeight}`;

      axios.get(url).then((response) => {
        if (response.data) {
          const blockTxHashes = response.data.tx_hashes.map(
            (tx_hash) => tx_hash.hash_id,
          );
          setTxHashes((prevTxHashes) => [...prevTxHashes, ...blockTxHashes]);
          setCurrentBlockHeight(blockHeight);
          setBlocks((prevBlocks) => {
            return [...prevBlocks, response.data];
          });

          blockTxHashes.forEach((txHash) => {
            fetchTransaction(txHash);
          });
        }
      });
    },
    [fetchTransaction],
  );

  useEffect(() => {
    if (lastBlockHeight) {
      fetchBlock(lastBlockHeight);
    }
  }, [lastBlockHeight, fetchBlock]);

  useEffect(() => {
    const displayCount = 20;
    if (currentBlockHeight && txHashes.length < displayCount) {
      fetchBlock(currentBlockHeight - 1);
    }
  }, [txHashes, currentBlockHeight, fetchBlock]);

  const onSearch = (txHash) => {
    if (!txHash) {
      return;
    }

    setIsSearching(true);
    setIsSearched(true);
    setSearchedTransaction(null);

    const url = `${txUrl}/${txHash}`;
    axios
      .get(url)
      .then((response) => {
        if (response.data) {
          setSearchedTransaction(response.data);
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsSearching(false);
      });
  };

  const getDataSource = useCallback(() => {
    if (isSearched && searchedTransaction) {
      return [searchedTransaction];
    } else if (isSearched && !searchedTransaction) {
      return [];
    }

    return transactions;
  }, [isSearched, searchedTransaction, transactions]);

  const getTransformedData = (data) => {
    const transformedData = data.map((tx) => {
      const blockHeight = getBlockHeightFromBlockHash(tx.block_id);
      const blockTime = getBlockTimeFromBlockHash(tx.block_id);

      const txHash = tx.hash;
      const txType = tx.tx_type;

      return {
        hash: txHash,
        block_id: tx.block_id,
        tx_type: txType,
        time: blockTime,
        block_height: blockHeight,
      };
    });

    return transformedData
      .sort((a, b) => b.block_height - a.block_height)
      .slice(0, 20);
  };

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      fetchLastBlock();
    }
  });

  return (
    <Box mt={16}>
      <Card>
        <h1>Transactions</h1>
        <SearchInput
          placeholder="Enter transaction hash"
          enterButton="Search"
          size="large"
          onSearch={onSearch}
          isLoading={isSearching}
          disabled={isSearching}
        />
        <Table
          style={{ marginTop: "16px" }}
          columns={columns}
          dataSource={getTransformedData(getDataSource())}
          rowKey="hash"
          pagination={false}
        />
      </Card>
    </Box>
  );
}

export default TransactionTable;
